import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { FaArrowRight, FaCheckCircle } from 'react-icons/fa';

interface OnboardingCardProps {
  step: number;
  totalSteps: number;
  title: string;
  message: string;
  visual?: React.ReactNode;
  onNext: () => void;
  onSkip?: () => void; 
  onFinish: () => void;
}

const cardVariants = {
  hidden: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  visible: {
    x: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 50, damping: 20 },
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
    transition: { duration: 0.3 },
  }),
};

const OnboardingCard: React.FC<OnboardingCardProps> = ({
  step,
  totalSteps,
  title,
  message,
  visual,
  onNext,
  onSkip,
  onFinish,
}) => {
  const isLastStep = step === totalSteps;

  return (
    <motion.div
      key={step} // Important for AnimatePresence to track items
      custom={1} // Assuming forward direction for now, can be enhanced
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="bg-gray-800/80 backdrop-blur-md shadow-2xl rounded-xl p-8 md:p-12 max-w-lg w-full text-white border border-gray-700"
    >
      <div className="text-center mb-6">
        {visual && <div className="mb-4 text-5xl text-cyan-400 mx-auto w-fit">{visual}</div>}
        <h2 className="text-3xl font-bold text-cyan-400 mb-2">{title}</h2>
        <p className="text-gray-300 text-lg">{message}</p>
      </div>

      <div className="flex justify-between items-center mt-8">
        {onSkip && !isLastStep && (
          <Button variant="ghost" onClick={onSkip} className="text-gray-400 hover:text-white">
            Skip
          </Button>
        )}
        {isLastStep && <div />} {/* Placeholder to keep Finish button to the right */}
        
        <Button 
          onClick={isLastStep ? onFinish : onNext} 
          className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold group"
        >
          {isLastStep ? 'Get Started' : 'Next'}
          {isLastStep ? <FaCheckCircle className="ml-2" /> : <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />}
        </Button>
      </div>
      
      {/* Progress Dots */}
      <div className="flex justify-center space-x-2 mt-8">
        {Array.from({ length: totalSteps }).map((_, index) => (
          <div
            key={index}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${ 
              index + 1 === step ? 'bg-cyan-500 scale-125' : 'bg-gray-600'
            }`}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default OnboardingCard;
